import { useEffect, useState } from "react";
import { useLocation, Link } from "wouter";
import { subscriptionAllowsAccess, useAuth } from "@/context/auth";
import { trackEvent } from "@/lib/analytics";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, ArrowRight, ArrowLeft, Sparkles, Eye, EyeOff, Store, Gem, ChefHat, Ruler, Pill, Smartphone, HardHat, Wind, Layers, GraduationCap } from "lucide-react";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");
const LOGO = `${import.meta.env.BASE_URL}logo.png`;

const SECTOR_BACKGROUND_IMAGES: Record<string, string> = {
  supermarket: `${BASE}/bg-supermarket.png`,
  fashion: `${BASE}/bg-fashion.png`,
  food: `${BASE}/bg-food.png`,
  salon: `${BASE}/bg-salon.png`,
  tailor: `${BASE}/bg-tailor.png`,
  pharmacy: `${BASE}/bg-pharmacy.png`,
  gadget_repair: `${BASE}/bg-gadget.png`,
  building: `${BASE}/bg-building.png`,
  laundry: `${BASE}/bg-laundry.png`,
  fabric_textile: `${BASE}/bg-fabric.png`,
};

const STORE_TYPES_GRID = [
  { id: 12, Icon: GraduationCap, name: "School & Campus", subtext: "Campus shops, student fees & term balances", category: "retail" as const, slug: "school_campus", gradient: "from-blue-500 via-indigo-500 to-violet-600" },
  { id: 1, Icon: Store,      name: "Supermarket & Grocery",  subtext: "High-volume stock, multi-brand POS & supplier management",  category: "essential" as const, slug: "supermarket",  gradient: "from-green-500 via-emerald-500 to-teal-600"    },
  { id: 2, Icon: Gem,        name: "Fashion & Ready-to-Wear", subtext: "Boutique collections, sizes/colours, outfit bundles & quick checkout", category: "retail" as const, slug: "fashion", gradient: "from-pink-500 via-rose-500 to-rose-700" },
  { id: 4, Icon: ChefHat,    name: "Food, Buka & Fresh Mart",subtext: "Cooked meals, frozen proteins, bulk grain unit breakdowns", category: "services"  as const, slug: "food",        gradient: "from-red-500 via-orange-500 to-amber-600"      },
  { id: 5, Icon: Sparkles,   name: "Salon & Beauty",         subtext: "Service-based selling, appointments & staff commissions",  category: "services"  as const, slug: "salon",       gradient: "from-fuchsia-500 via-pink-500 to-rose-600"     },
  { id: 6, Icon: Ruler,      name: "Tailor & Bespoke",       subtext: "Order tracking, fabric stock & measurement records",       category: "services"  as const, slug: "tailor",      gradient: "from-violet-500 via-purple-500 to-purple-700"  },
  { id: 7, Icon: Pill,       name: "Pharmacy",               subtext: "Expiry tracking, digital prescriptions & batch control",   category: "essential" as const, slug: "pharmacy",      gradient: "from-teal-400 via-cyan-500 to-cyan-700"        },
  { id: 8, Icon: Smartphone, name: "Gadgets & Repair",       subtext: "Serial/IMEI tracking, technician logs & repair status",   category: "services"  as const, slug: "gadget_repair",gradient: "from-slate-500 via-blue-600 to-indigo-700"     },
  { id: 9, Icon: HardHat,    name: "Building Materials",     subtext: "Bulk wholesale, warehouse stock & waybill logistics",      category: "retail"    as const, slug: "building",     gradient: "from-amber-500 via-orange-500 to-orange-700"  },
  { id: 10,Icon: Wind,       name: "Laundry & Dry Cleaning", subtext: "Garment tagging, fabric care stages & pickup alerts",     category: "services"  as const, slug: "laundry",         gradient: "from-sky-400 via-blue-500 to-blue-600"        },
  { id: 11,Icon: Layers,     name: "Fabric & Textile",       subtext: "Ankara, Lace, George & rolls sold by the yard to tailors", category: "retail"    as const, slug: "fabric_textile", gradient: "from-purple-500 via-violet-500 to-indigo-600"  },
];

export default function Login() {
  const [, setLocation] = useLocation();
  const { refetch } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const doLogin = async (loginEmail: string, loginPassword: string) => {
    const res = await fetch(`${BASE}/api/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ email: loginEmail.trim(), password: loginPassword }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || "Login failed");
    await refetch();
    trackEvent("login_succeeded", {
      login_type: "standard",
      access_active: subscriptionAllowsAccess(data.store),
    });
    setLocation(subscriptionAllowsAccess(data.store) ? "/" : "/subscribe");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await doLogin(email, password);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#0B1120] p-4">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(37,99,235,0.16),transparent_48%),radial-gradient(circle_at_100%_100%,rgba(79,70,229,0.12),transparent_42%)]" />
      <div className="relative z-10 w-full max-w-md">
        <div className="flex items-center justify-center gap-3 mb-8">
          <img src={LOGO} alt="RetailPulsePOS" className="w-10 h-10 rounded-xl object-cover" />
          <span className="text-2xl font-bold text-white">RetailPulsePOS</span>
        </div>

        <Link
          href="/demo"
          className="mb-6 flex items-center justify-center gap-2 rounded-xl border border-cyan-400/40 bg-cyan-400/10 px-4 py-3 text-center text-sm font-semibold text-cyan-200 transition-colors hover:border-cyan-300/60 hover:bg-cyan-400/20 hover:text-white"
        >
          <Sparkles className="h-4 w-4 shrink-0" />
          Exploring RetailPulsePOS? Try our Instant Live Demos →
        </Link>

        <Card className="border-white/10 bg-white shadow-2xl">
          <CardHeader className="pb-4 text-center">
            <CardTitle className="text-2xl text-slate-900">Welcome back</CardTitle>
            <CardDescription className="text-base text-slate-600">Sign in to your RetailPulsePOS account</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="login-email" className="text-sm font-semibold text-slate-800">Email</label>
                <Input id="login-email" type="email" inputMode="email" autoCapitalize="none" autoCorrect="off" spellCheck={false} placeholder="you@yourstore.com" value={email} onChange={e => setEmail(e.target.value)} required autoComplete="email" className="mt-1.5 h-11 border-slate-300 bg-white text-slate-900 placeholder:text-slate-400" />
              </div>
              <div>
                <div className="mb-1.5 flex items-center justify-between">
                  <label htmlFor="login-password" className="text-sm font-semibold text-slate-800">Password</label>
                  <Link href="/forgot-password" className="text-xs font-medium text-indigo-600 transition-colors hover:text-indigo-800">
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <Input id="login-password" type={showPassword ? "text" : "password"} placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} required autoComplete="current-password" className="h-11 border-slate-300 bg-white pr-10 text-slate-900 placeholder:text-slate-400" />
                  <button type="button" aria-label={showPassword ? "Hide password" : "Show password"} onClick={() => setShowPassword(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 transition-colors hover:text-slate-700">
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              {error && <p role="alert" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
              <Button type="submit" className="h-11 w-full gap-2" disabled={loading}>
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
                Sign In
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="text-center text-sm text-slate-500 mt-6">
          No account?{" "}
          <Link href="/signup" className="text-indigo-400 hover:underline">Start free trial</Link>
        </p>
      </div>
    </div>
  );
}

export function Demo() {
  const [activeFilter, setActiveFilter] = useState<"all" | "retail" | "services" | "essential">("all");
  const [hoveredStore, setHoveredStore] = useState<(typeof STORE_TYPES_GRID)[number] | null>(null);
  const [activeStore, setActiveStore] = useState<(typeof STORE_TYPES_GRID)[number] | null>(null);

  const filteredStores = STORE_TYPES_GRID.filter(
    store => activeFilter === "all" || store.category === activeFilter,
  );
  const backgroundStore = hoveredStore ?? activeStore;
  const [renderedBackground, setRenderedBackground] = useState<typeof backgroundStore>(null);
  const [backgroundVisible, setBackgroundVisible] = useState(false);
  const renderedBackgroundImage = renderedBackground
    ? SECTOR_BACKGROUND_IMAGES[renderedBackground.slug]
    : undefined;

  useEffect(() => {
    setBackgroundVisible(false);
    const swapTimer = window.setTimeout(() => {
      setRenderedBackground(backgroundStore);
      setBackgroundVisible(Boolean(backgroundStore));
    }, backgroundStore ? 250 : 0);

    return () => window.clearTimeout(swapTimer);
  }, [backgroundStore?.slug]);

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#0B1120] px-4 py-8 sm:py-10">
      <div
        aria-hidden="true"
        className={`pointer-events-none absolute inset-0 bg-cover bg-center bg-no-repeat transition-opacity duration-500 ${backgroundVisible ? "opacity-100" : "opacity-0"}`}
        style={renderedBackgroundImage ? { backgroundImage: `url(${renderedBackgroundImage})` } : undefined}
      />
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-black/60" />
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-gradient-to-b from-slate-950/35 via-transparent to-slate-950/50" />

      <div className="relative z-10 mx-auto w-full max-w-5xl">
        <div className="mb-8 flex items-center justify-center gap-3">
          <img src={LOGO} alt="RetailPulsePOS" className="h-10 w-10 rounded-xl object-cover" />
          <span className="text-2xl font-bold text-white">RetailPulsePOS</span>
        </div>

        <div className="mb-8 text-center">
          <Link href="/login" className="mb-5 inline-flex items-center gap-1.5 text-sm text-slate-400 transition-colors hover:text-white">
            <ArrowLeft className="h-4 w-4" />
            Back to sign in
          </Link>
          <h1 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">Explore our live demos</h1>
          <p className="mx-auto mt-2 max-w-2xl text-base text-slate-300">
            Choose an industry to see how RetailPulsePOS helps Nigerian businesses manage sales, stock, and operations.
          </p>
        </div>

        <div className="mb-6 flex flex-wrap items-center justify-center gap-2">
          {([
            { label: "All Businesses", tag: "all" },
            { label: "Retail / Boutique", tag: "retail" },
            { label: "Services / Repairs", tag: "services" },
            { label: "Essential / Health", tag: "essential" },
          ] as const).map(pill => (
            <button
              key={pill.tag}
              onClick={() => setActiveFilter(pill.tag)}
              className={`rounded-full border px-4 py-1.5 text-sm font-medium transition-all ${
                activeFilter === pill.tag
                  ? "border-violet-500 bg-violet-600 text-white shadow-lg shadow-violet-950/40"
                  : "border-white/15 bg-white/5 text-slate-300 hover:bg-white/10 hover:text-white"
              }`}
            >
              {pill.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filteredStores.map(store => (
            <Link
              key={store.id}
              href={`/login/${store.slug}`}
              className="group block h-full"
              onMouseEnter={() => setHoveredStore(store)}
              onMouseLeave={() => setHoveredStore(null)}
              onFocus={() => setHoveredStore(store)}
              onBlur={() => setHoveredStore(null)}
              onClick={() => setActiveStore(store)}
            >
              <div className="flex h-full flex-col overflow-hidden rounded-2xl border border-white/10 bg-white/[0.07] transition-all duration-200 hover:-translate-y-0.5 hover:border-white/25 hover:bg-white/[0.12]">
                <div className={`relative flex h-32 shrink-0 items-center justify-center overflow-hidden bg-gradient-to-br ${store.gradient}`}>
                  <div className="absolute inset-0 bg-black/15" />
                  <div className="relative flex h-16 w-16 items-center justify-center rounded-2xl bg-white/20 shadow-[0_0_0_1px_rgba(255,255,255,0.25),0_8px_24px_rgba(0,0,0,0.25)] backdrop-blur-sm">
                    <store.Icon className="relative h-8 w-8 text-white drop-shadow-[0_2px_6px_rgba(0,0,0,0.4)]" />
                  </div>
                </div>
                <div className="flex flex-1 flex-col p-4">
                  <p className="mb-1.5 text-base font-bold leading-snug text-white">{store.name}</p>
                  <p className="flex-1 text-sm leading-relaxed text-slate-200">{store.subtext}</p>
                  <div className="mt-4 border-t border-white/10 pt-3">
                    <span className="text-sm font-semibold text-cyan-300 transition-colors group-hover:text-cyan-200">
                      Launch Demo →
                    </span>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
